import { useEffect, useState } from "react";
import axios from "axios";
import useReveal, { useCountUp } from "../../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  { metric: "200+", label: "Products globally" },
  { metric: "25", label: "Years of expertise" },
  { metric: "500+", label: "Healthcare partners" },
  { metric: "12", label: "International markets" },
];

/**
 * TrustedBy — slim animated counter strip rendered above the global Footer.
 * Reuses `home.achievements` data so admin edits propagate everywhere.
 */
export default function TrustedBy() {
  const [items, setItems] = useState(FALLBACK);
  const { ref, visible, revealKey } = useReveal();

  useEffect(() => {
    axios.get(`${API}/public/pages/home.achievements`)
      .then((r) => {
        const list = r.data?.value?.items;
        if (Array.isArray(list) && list.length) setItems(list);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      ref={ref}
      data-testid="trusted-by-strip"
      className="relative overflow-hidden bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0369A1] text-white"
    >
      <div className="absolute inset-0 opacity-[0.06]" aria-hidden style={{
        backgroundImage: "radial-gradient(circle at 25% 25%, white 1px, transparent 1.5px), radial-gradient(circle at 75% 75%, white 1px, transparent 1.5px)",
        backgroundSize: "44px 44px",
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-14">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 lg:gap-12">
          <div
            className={`max-w-md transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FB923C] font-semibold">Trusted by</div>
            <h3 className="mt-2 text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              The numbers behind <span className="text-[#FB923C]">Bonheur</span>
            </h3>
            <p className="mt-2 text-sm text-white/70">Real impact across clinicians, hospitals and distributors.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 lg:gap-10 flex-1">
            {items.slice(0, 4).map((it, i) => (
              <Counter key={i} item={it} trigger={revealKey} visible={visible} delay={i * 100} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Counter({ item, trigger, visible, delay }) {
  const animated = useCountUp(item.metric, trigger, 1200);
  return (
    <div
      className="border-l-2 border-white/20 pl-4 transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)", transitionDelay: `${delay}ms` }}
      data-testid="trusted-counter"
    >
      <div className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>{animated}</div>
      <div className="mt-1 text-[11px] sm:text-xs text-white/70 leading-snug">{item.label}</div>
    </div>
  );
}
